/**
 * The screenshots Claude Code took (`forge_screenshot` frames), in the side
 * panel: the newest as a preview, the rest as thumbnails, and a count of the
 * ones not looked at yet. Any of them opens the full-size viewer
 * (`ForgeScreenshotViewer`). Everything is in-memory for the page session —
 * the runtime keeps the files in its workspace.
 */
import { Expand } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { ForgeScreenshot } from '../types';

interface ForgeScreenshotsProps {
  screenshots: ForgeScreenshot[];
  /** Screenshots that arrived since the viewer was last opened. */
  unseen?: number;
  /** Open the viewer at this screenshot. */
  onOpen: (index: number) => void;
  onClear?: () => void;
}

export function ForgeScreenshots({
  screenshots,
  unseen = 0,
  onOpen,
  onClear,
}: ForgeScreenshotsProps) {
  const latestIndex = screenshots.length - 1;
  const latest = screenshots[latestIndex];

  return (
    <div className="rounded-lg border p-3 space-y-2" data-testid="forge-screenshots">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-medium flex items-center gap-2">
          Screenshots
          {unseen > 0 && (
            <Badge variant="secondary" data-testid="forge-screenshots-unseen">
              {unseen} new
            </Badge>
          )}
        </h3>
        {screenshots.length > 0 && onClear && (
          <Button size="sm" variant="ghost" onClick={onClear}>
            Clear
          </Button>
        )}
      </div>
      {!latest ? (
        <p className="text-xs text-muted-foreground">
          Claude Code sends a screenshot whenever it looks at the running app.
        </p>
      ) : (
        <>
          <button
            type="button"
            onClick={() => onOpen(latestIndex)}
            className="group relative block w-full overflow-hidden rounded border bg-background"
            aria-label="Open the latest screenshot"
          >
            <img src={latest.dataUrl} alt={`Screenshot ${latest.path}`} className="w-full" />
            <span className="absolute right-1 top-1 rounded bg-background/80 p-1 opacity-0 transition-opacity group-hover:opacity-100">
              <Expand className="h-4 w-4" />
            </span>
          </button>
          {screenshots.length > 1 && (
            <div className="flex gap-1 overflow-x-auto">
              {screenshots.map((s, i) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => onOpen(i)}
                  className="h-12 w-16 flex-shrink-0 overflow-hidden rounded border opacity-80 hover:opacity-100"
                  aria-label={`Open screenshot ${i + 1}`}
                  title={s.path}
                >
                  <img src={s.dataUrl} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => onOpen(latestIndex)}
          >
            View screenshots ({screenshots.length})
          </Button>
        </>
      )}
    </div>
  );
}
